import { useEffect, useState, type MutableRefObject } from "react";
import { db } from "../firebase";
import { PairingClient, type GroupChannel } from "../protocol";
import type { PairingPayload } from "../pairings";

/** A group paired with the pairing it lives in — groups are pairing-level, so routing messages/creates needs
 *  the pairingId alongside the group. */
export interface GroupEntry {
  pairingId: string;
  group: GroupChannel;
}

/** All #Group-chat channels across every mirrored pairing, merged + sorted by recency. Groups live outside any
 *  session (pairings/{pid}/groups), so this is a pairing-level subscription separate from useSessions. */
export function useGroups(
  clients: MutableRefObject<Map<string, PairingClient>>,
  ready: boolean,
  pairings: PairingPayload[],
): GroupEntry[] {
  const [groups, setGroups] = useState<GroupEntry[]>([]);
  useEffect(() => {
    if (!ready) return;
    const unsubs: Array<() => void> = [];
    const byPairing = new Map<string, GroupChannel[]>();
    let cancelled = false;
    void (async () => {
      for (const p of pairings) {
        // Own the client creation here rather than relying on useSessions having finished first — otherwise
        // this effect races that async creation, finds no client, and the group subscription never arms (the
        // "created a group, nothing happened" bug). The clients map is shared + keyed, so this is idempotent.
        if (!clients.current.has(p.pairingId)) clients.current.set(p.pairingId, await PairingClient.create(db, p));
        if (cancelled) return;
        unsubs.push(
          clients.current.get(p.pairingId)!.subscribeGroups((list) => {
            byPairing.set(p.pairingId, list);
            const all: GroupEntry[] = [...byPairing.entries()].flatMap(([pid, gs]) => gs.map((group) => ({ pairingId: pid, group })));
            all.sort((a, b) => b.group.updatedAt - a.group.updatedAt);
            setGroups(all);
          }),
        );
      }
    })();
    return () => {
      cancelled = true;
      unsubs.forEach((u) => u?.());
    };
  }, [ready, pairings]);
  return groups;
}
